import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore } from '@/stores/AuthStore';
import {
  flushAnalyticsQueues,
  getAnalyticsStatus,
  setAnalyticsIdentity,
  startAnalyticsClient,
  trackAnalyticsEvent,
  trackAnalyticsPageView,
} from './client';
import { getPageTrackingData, isTeamBootstrapRoute } from './routeTracking';
import { getAnalyticsIdentity } from './identity';
import type { AnalyticsParams, AnalyticsStatus } from './types';

interface AnalyticsContextValue {
  trackEvent: (eventName: string, params?: AnalyticsParams) => void;
  getStatus: () => AnalyticsStatus;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isAuthenticated = useAuthStore(state => state.status === 'authenticated');

  useEffect(() => startAnalyticsClient(), []);

  useEffect(() => {
    setAnalyticsIdentity(getAnalyticsIdentity(isAuthenticated));
  }, [isAuthenticated]);

  useEffect(() => {
    if (isTeamBootstrapRoute(location.pathname)) return;

    const trackingData = getPageTrackingData(location.pathname);
    if (
      trackingData.page_restrictions === 'restricted' &&
      useAuthStore.getState().sessionState !== 'authenticated'
    ) {
      return;
    }

    trackAnalyticsPageView(location.pathname, location.search, trackingData);
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    const debugApi = {
      flush: flushAnalyticsQueues,
      getStatus: getAnalyticsStatus,
    };
    window.dtAnalytics = debugApi;

    return () => {
      if (window.dtAnalytics === debugApi) delete window.dtAnalytics;
    };
  }, []);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      trackEvent: trackAnalyticsEvent,
      getStatus: getAnalyticsStatus,
    }),
    []
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) throw new Error('useAnalytics must be used inside AnalyticsProvider');
  return context;
};
